import React, { useCallback, useState } from 'react';
import { Button, Text, Layer, Box, Image } from 'grommet';
import { DownloadOption, Close } from 'grommet-icons';
import { isiOS } from '../helpers/browserDetect';
import { analytics } from '../services/analytics';
import { usePaywallContext } from '@contexts/PaywallContext';
import {
  PaywallPageContext,
  ViewerEntitlementStatus,
} from '../components/Paywall/paywall.types';

import useDownloadClips from '../hooks/useDownloadClips';
import {
  DownloadButtonProps,
  DownloadInstructionsProps,
} from './downloadButton.types';

const DownloadButton: React.FC<DownloadButtonProps> = ({
  id,
  isUnclaimed,
  trackEvent,
  player,
  game,
  hitMatchData,
  setShowUnclaimedModal,
  isPreview = false,
  entitlementStatus,
}) => {
  const { downloadClips, isAppHosted } = useDownloadClips();

  const [showDownloadInstructions, setShowDownloadInstructions] =
    useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  let paywallPageContext = PaywallPageContext.SINGLE_CLIP_PAGE;
  let paywallTarget = player?.playerId;

  if (game) {
    paywallPageContext = PaywallPageContext.GAME_SUMMARY;
    paywallTarget = undefined;
  }

  const { handlePaywallOpen, setHandleLockModal } = usePaywallContext({
    entitlementStatus,
    trackingContext: paywallPageContext,
    target: paywallTarget,
  });

  const handleDownloadUrl = useCallback(async () => {
    setIsDownloading(true);
    let clipDownloadUrl = '';
    let fileName = '';
    let trackText = '';

    if (player && player.playerClipObj) {
      const { playerClipObj } = player;
      clipDownloadUrl = isPreview
        ? playerClipObj.fileDownloadUrl || ''
        : playerClipObj.fileDownloadUrlFull ||
          playerClipObj.fileDownloadUrl ||
          '';

      fileName =
        playerClipObj?.textDescription?.toLowerCase().replace(/ /g, '_') ||
        playerClipObj.textDescriptionBrief;

      trackText = 'Player Clip';
    }

    if (game && game.gameHighlightObj) {
      const { gameHighlightObj } = game;
      clipDownloadUrl = gameHighlightObj.fileUrl720 || '';
      fileName = gameHighlightObj.titleMain.toLowerCase().replace(/ /g, '_');
      trackText = 'Game Summary';
    }

    if (hitMatchData) {
      const { hitMatchDataObj } = hitMatchData;
      clipDownloadUrl = hitMatchDataObj?.fileUrl720 || '';
      fileName = `eoy-highlights-${hitMatchDataObj?.playerName.toLowerCase().replace(/ /g, '-')}`;
      trackText = 'End of Year Highlilght Video';
    }

    analytics.track(`${trackText} Download Clicked`, {
      ...trackEvent,
    });

    const downloadUrl = await downloadClips(clipDownloadUrl, fileName);

    if (downloadUrl) {
      setIsDownloading(false);
      return downloadUrl;
    }
  }, [downloadClips, game, isPreview, player, trackEvent, hitMatchData]);

  const onDownloadClicked = (event: React.SyntheticEvent) => {
    if (entitlementStatus === ViewerEntitlementStatus.ENTITLED) {
      onEntitledDownloadClicked();
    } else if (
      entitlementStatus === ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN
    ) {
      // show paywall when logged in
      handlePaywallOpen();
    } else {
      // show lock modal when not logged in
      setHandleLockModal(true);
    }

    event.preventDefault();
  };

  const onEntitledDownloadClicked = () => {
    if (isUnclaimed) {
      setShowUnclaimedModal && setShowUnclaimedModal(true);
    } else {
      handleDownloadUrl();
      setShowDownloadInstructions(true);
    }
  };

  return (
    <>
      <Button
        id={id}
        plain
        color="secondary-1"
        icon={<DownloadOption size="20px" color="secondary-1" />}
        label={
          <Text size="small">
            {isDownloading && !isAppHosted() ? '...Downloading' : 'Download'}
          </Text>
        }
        href={`#`}
        onClick={(event: React.SyntheticEvent) => {
          onDownloadClicked(event);
        }}
      />
      {showDownloadInstructions && isiOS() && !isAppHosted() && (
        <DownloadInstructions
          setShowDownloadInstructions={setShowDownloadInstructions}
        />
      )}
    </>
  );
};

const DownloadInstructions: React.FC<DownloadInstructionsProps> = ({
  setShowDownloadInstructions,
}) => {
  return (
    <Layer onEsc={() => setShowDownloadInstructions(false)}>
      <Box>
        <Box direction="row" justify="end" fill="horizontal">
          <Button
            hoverIndicator
            icon={<Close />}
            onClick={() => setShowDownloadInstructions(false)}
          />
        </Box>
        <Box margin="small">
          <Text>
            Your video will appear in the download list in the top right corner
            of the screen after you press the down arrow.
          </Text>
          <Image src="/download_clip_help.png" />
        </Box>
      </Box>
    </Layer>
  );
};

export default DownloadButton;
